/**
 * Toast Store
 *
 * Centralized toast notification management using vue-sonner.
 * Provides a clean API for showing success, error, warning, and info toasts.
 *
 */

import { defineStore } from "pinia";
import { toast } from "vue-sonner";
import i18n from '@/i18n/i18n.js';
import { userFacingError } from '@/utils/userFacingError';

const safeError = (message: unknown) => userFacingError(message, i18n.global.t('errors.oopsSomethingWentWrong'));

export const useToastStore = defineStore("toast", () => {
    //  Show a success toast
    const success = (message: string, description?: string) => {
        toast.success(message, { description });
    };

    //  Show an error toast
    const error = (message: string, description?: string) => {
        toast.error(safeError(message), { description: description ? safeError(description) : undefined });
    };

    //  Show an info toast
    const info = (message: string, description?: string) => {
        toast.info(message, { description });
    };

    //  Show a warning toast
    const warning = (message: string, description?: string) => {
        toast.warning(safeError(message), { description: description ? safeError(description) : undefined });
    };

    //  Show a loading toast
    const loading = (message: string) => {
        return toast.loading(message);
    };

    /**
     * Show a promise toast with loading/success/error states
     * @param promise - Promise to track
     * @param messages - Messages for each state
     */
    const promise = <T>(
        promise: Promise<T> | (() => Promise<T>),
        messages: {
            loading: string;
            success: string | ((data: T) => string);
            error: string | ((error: unknown) => string);
        },
    ) => {
        const errorMessage = messages.error;
        return toast.promise(promise, {
            ...messages,
            error: (error: unknown) => safeError(typeof errorMessage === 'function' ? errorMessage(error) : errorMessage),
        });
    };

    /**
     * Dismiss a specific toast by ID
     * @param toastId - ID of toast to dismiss
     */
    const dismiss = (toastId?: string | number) => {
        toast.dismiss(toastId);
    };

    return {
        success,
        error,
        info,
        warning,
        loading,
        promise,
        dismiss,
    };
});
